import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider
} from 'react-router-dom';
import {
  Box,
  CssBaseline,
  CircularProgress
} from '@mui/material';
import {
  createTheme,
  ThemeProvider
} from '@mui/material/styles';
import AuthenticationProvider from './contexts/authentication';
import SocketProvider from './contexts/socket';
import ProtectedRoute from './routes/protected';
import {loaderById as RestaurantLoaderById} from './routes/restaurant';
import {loaderByName as RestaurantLoaderByName} from './routes/restaurant';
import './i18n';

const Login = lazy(() => import ('./routes/login'));
const Admin = lazy(() => import('./routes/admin'));
const Restaurant = lazy(() => import('./routes/restaurant'));
const Terminal = lazy(() => import('./routes/terminal'));
const Dashboard = lazy(() => import('./routes/dashboard'));
const Unsubscribe = lazy(() => import('./routes/unsubscribe'));
const Error = lazy(() => import('./routes/error'));

const theme = createTheme({
  palette: {
    background: {
      default: '#fdf7eb',
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4B7359'
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#4B7359',
          '&.Mui-disabled': {
            backgroundColor: '#ded9ce',
          },
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: '#4B7359',
        },
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#000000'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#4B7359',
          '&.Mui-selected': {
            color: '#000000'
          }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#000000',
          '&.Mui-focused': {
            color: '#4B7359',
          },          
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#4B7359'
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#4B7359',
          '&.Mui-checked': {
            color: '#4B7359', // Checked color
          },            
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#4B7359',
          '&.Mui-checked': {
            color: '#4B7359', // Checked color
          },          
        }
      }
    }
  }
});

const withSuspense = (element) => (
  <Suspense
    fallback={
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100vw">
        <CircularProgress sx={{ color: '#4B7359' }} />
      </Box>}>
      {element}
  </Suspense>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: withSuspense(<Login />),
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/unsubscribe',
    element: withSuspense(<Unsubscribe />),
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/admin',
    element: <ProtectedRoute element={withSuspense(<Admin />)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/:restaurantName/',
    element: withSuspense(<Restaurant />),
    errorElement: withSuspense(<Error />),
    loader: RestaurantLoaderByName,
  },
  {
    path: '/restaurant/:restaurantId/seat/:seatId',
    element: withSuspense(<Restaurant />),
    errorElement: withSuspense(<Error />),
    loader: RestaurantLoaderById,
    caseSensitive: true,
  },
  {
    path: '/restaurant/:restaurantId/terminal',
    element: (
      <ProtectedRoute
        element={
          <SocketProvider>
            {withSuspense(<Terminal />)}
          </SocketProvider>
        }
      />
    ),
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='home'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/menus',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='menus'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/menus/:menuId',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='menus' child='details'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/menus/:menuId/categories/:categoryId',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='menus' child='details'/>)} />,
    errorElement: withSuspense(<Error />),
  },    
  {
    path: '/restaurant/dashboard/menus/:menuId/categories/:categoryId/items/:itemId',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='menus' child='items'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/modifiers',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='modifiers'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/modifiers/:modifierId',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='modifiers'/>)} />,
    errorElement: withSuspense(<Error />),
  },    
  {
    path: '/restaurant/dashboard/terminals',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='terminals'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/seats',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='seats'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/users',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='users'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/users/:userId',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='users' child='details'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/orders',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='orders'/>)} />,
    errorElement: withSuspense(<Error />),
  },
  {
    path: '/restaurant/dashboard/orders/:orderId',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='orders' child='details'/>)} />,
    errorElement: withSuspense(<Error />),
  },  
  {
    path: '/restaurant/dashboard/payments',
    element: <ProtectedRoute element={withSuspense(<Dashboard element='payments'/>)} />,
    errorElement: withSuspense(<Error />),
  }
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  
    <React.StrictMode>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthenticationProvider>
            <RouterProvider router={router} />
          </AuthenticationProvider>
        </ThemeProvider>
    </React.StrictMode>
);
